import IconManager from './iconSystem';
/**
 * Modal Manager - Utility for managing the new modal component system
 * Integrates with Microsoft Fluent Design modal components
 */

import componentLifecycleManager from './componentLifecycleManager.js';
import { IconUtils } from '../constants/icons.js';

class ModalManager {
  constructor() {
    this.activeModal = null;
    this.focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    
    // Register component with lifecycle manager
    componentLifecycleManager.registerComponent('ModalManager', this);
    this.lifecycle = componentLifecycleManager.createMixin('ModalManager');
    
    this.initializeEventListeners();
  }

  /**
   * Initialize global event listeners for modals
   */
  initializeEventListeners() {
    // Handle escape key using lifecycle manager
    this.lifecycle.addEventListener(document, 'keydown', (e) => {
      if (e.key === 'Escape' && this.activeModal) {
        this.closeModal(this.activeModal.id);
      }
    });

    // Handle backdrop clicks using lifecycle manager
    this.lifecycle.addEventListener(document, 'click', (e) => {
      if (e.target.classList.contains('modal__backdrop') && this.activeModal) {
        this.closeModal(this.activeModal.id);
      }
    });
  }

  /**
   * Open a modal with enhanced functionality
   * @param {string} modalId - The ID of the modal to open
   * @param {Object} options - Configuration options
   */
  openModal(modalId, options = {}) {
    const modal = document.getElementById(modalId);
    if (!modal) {
      console.error(`Modal with ID '${modalId}' not found`);
      return;
    }

    // Close any existing modal
    if (this.activeModal) {
      this.closeModal(this.activeModal.id, { silent: true });
    }

    // Store previously focused element
    this.previouslyFocused = document.activeElement;

    // Show modal
    modal.style.display = 'flex';
    modal.classList.add('show');
    modal.setAttribute('aria-hidden', 'false');

    // Add entering animation class
    modal.classList.add('entering');
    setTimeout(() => {
      modal.classList.remove('entering');
    }, 300);

    // Set active modal
    this.activeModal = modal;

    // Focus management
    this.focusModal(modal);

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Trigger custom event
    modal.dispatchEvent(new CustomEvent('modal:opened', {
      detail: { modalId, options }
    }));
  }

  /**
   * Close a modal with enhanced functionality
   * @param {string} modalId - The ID of the modal to close
   * @param {Object} options - Configuration options
   */
  closeModal(modalId, options = {}) {
    const modal = document.getElementById(modalId);
    if (!modal) {
      console.error(`Modal with ID '${modalId}' not found`);
      return;
    }

    // Remove focus from any element inside the modal before hiding
    const focusedElement = modal.querySelector(':focus');
    if (focusedElement) {
      focusedElement.blur();
    }

    // Add exiting animation
    modal.classList.add('exiting');
    
    setTimeout(() => {
      // Hide modal
      modal.style.display = 'none';
      modal.classList.remove('show', 'exiting');
      modal.setAttribute('aria-hidden', 'true');

      // Clear active modal
      if (this.activeModal === modal) {
        this.activeModal = null;
      }

      // Restore body scroll
      document.body.style.overflow = '';

      // Restore focus
      if (this.previouslyFocused && !options.silent) {
        this.previouslyFocused.focus();
        this.previouslyFocused = null;
      }

      // Trigger custom event
      modal.dispatchEvent(new CustomEvent('modal:closed', {
        detail: { modalId, options }
      }));
    }, 200);
  }

  /**
   * Focus management for modals
   * @param {HTMLElement} modal - The modal element
   */
  focusModal(modal) {
    // Find first focusable element
    const focusableElements = modal.querySelectorAll(this.focusableElements);
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Focus first element or modal container
    if (firstFocusable) {
      firstFocusable.focus();
    } else {
      const container = modal.querySelector('.modal__container');
      if (container) {
        container.focus();
      }
    }

    // Trap focus within modal
    const handleFocusTrap = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      }
    };

    modal.addEventListener('keydown', handleFocusTrap);

    // Store cleanup function
    modal._focusTrapCleanup = () => {
      modal.removeEventListener('keydown', handleFocusTrap);
    };
  }

  /**
   * Get the currently active modal
   * @returns {HTMLElement|null} The active modal element
   */
  getActiveModal() {
    return this.activeModal;
  }

  /**
   * Check if any modal is currently open
   * @returns {boolean} True if a modal is open
   */
  isModalOpen() {
    return this.activeModal !== null;
  }

  /**
   * Close all open modals
   */
  closeAllModals() {
    if (this.activeModal) {
      this.closeModal(this.activeModal.id);
    }
  }

  /**
   * Show confirmation modal with promise-based API
   * @param {Object} config - Configuration for confirmation modal
   * @returns {Promise<boolean>} Promise that resolves with user choice
   */
  confirm(config = {}) {
    return new Promise((resolve) => {
      const {
        title = 'Confirm Action',
        message = 'Are you sure you want to proceed?',
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        type = 'warning' // warning, danger, info, success
      } = config;

      // Create modal if it doesn't exist
      let confirmModal = document.getElementById('globalConfirmModal');
      if (!confirmModal) {
        confirmModal = this.createConfirmModal();
        document.body.appendChild(confirmModal);
      }

      // Update modal content
      const titleEl = confirmModal.querySelector('.modal__title');
      const iconEl = confirmModal.querySelector('.modal__icon');
      const messageEl = confirmModal.querySelector('.modal__description');
      const confirmBtn = confirmModal.querySelector('.btn--danger, .btn--primary');
      const cancelBtn = confirmModal.querySelector('.btn--secondary');

      titleEl.textContent = title;
      messageEl.textContent = message;
      confirmBtn.textContent = confirmText;
      cancelBtn.textContent = cancelText;

      // Update icon and button classes based on type
      iconEl.className = `modal__icon modal__icon--${type}`;
      const iconMap = {
        warning: IconUtils.getSvg('warning'),
        danger: IconUtils.getSvg('warning'),
        info: IconUtils.getSvg('info'),
        success: IconUtils.getSvg('check')
      };
      iconEl.innerHTML = iconMap[type] || iconMap.warning;

      // Update button style
      confirmBtn.className = type === 'danger' ? 'btn btn--danger' : 'btn btn--primary';

      // Handle button clicks
      const handleConfirm = () => {
        cleanup();
        resolve(true);
      };

      const handleCancel = () => {
        cleanup();
        resolve(false);
      };

      const cleanup = () => {
        confirmBtn.removeEventListener('click', handleConfirm);
        cancelBtn.removeEventListener('click', handleCancel);
        this.closeModal('globalConfirmModal');
      };

      confirmBtn.addEventListener('click', handleConfirm);
      cancelBtn.addEventListener('click', handleCancel);

      // Open modal
      this.openModal('globalConfirmModal');
    });
  }

  /**
   * Create a global confirmation modal
   * @returns {HTMLElement} The created modal element
   */
  createConfirmModal() {
    const modal = document.createElement('div');
    modal.id = 'globalConfirmModal';
    modal.className = 'modal modal--confirmation';
    modal.style.display = 'none';
    modal.setAttribute('aria-hidden', 'true');

    modal.innerHTML = `
      <div class="modal__backdrop"></div>
      <div class="modal__container modal__container--small">
        <div class="modal__header">
          <h3 class="modal__title">Confirm Action</h3>
          <button class="modal__close" type="button" onclick="modalManager.closeModal('globalConfirmModal')">
            ${IconUtils.getSvg('close')}
          </button>
        </div>
        
        <div class="modal__content">
          <div class="modal__icon modal__icon--warning">
            ${IconUtils.getSvg('warning')}
          </div>
          <div class="modal__description">
            Are you sure you want to proceed?
          </div>
        </div>
        
        <div class="modal__actions">
          <button type="button" class="btn btn--secondary">Cancel</button>
          <button type="button" class="btn btn--danger">Confirm</button>
        </div>
      </div>
    `;

    return modal;
  }

  /**
   * Comprehensive cleanup using lifecycle manager
   */
  destroy() {
    if (this.activeModal) {
      this.closeModal(this.activeModal.id, { silent: true });
    }
    this.lifecycle.lifecycleCleanup();
    this.activeModal = null;
  }
}

// Create global instance
const modalManager = new ModalManager();

// Global functions for backward compatibility
window.openModal = (modalId, options) => modalManager.openModal(modalId, options);
window.closeModal = (modalId, options) => modalManager.closeModal(modalId, options);
window.modalConfirm = (config) => modalManager.confirm(config);

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ModalManager;
}

// Make available globally
window.ModalManager = ModalManager;
window.modalManager = modalManager;
